import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, Badge, Button as BSButton } from 'react-bootstrap';
import {
    ArrowLeftOutlined,
    MailOutlined,
    PhoneOutlined,
    SafetyOutlined,
    SolutionOutlined,
    WalletOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    EyeOutlined,
    DollarCircleOutlined,
    ContainerOutlined,
    ArrowRightOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { Button } from 'antd';
import { oneKlicks } from "../SDK/oneclick";
import useInsurancePayment from "../hooks/useInsurancePayment";

const normaliseResponse = (response) => {
    if (!response?.success) return [];
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    return [];
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const amount = Number(value);
    if (Number.isNaN(amount)) return value;
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
    }).format(amount);
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const calculateMonthlyPremium = (policy) => {
    if (!policy) return 0;
    const annual = Number(
        policy.premium_amount ??
        policy.annual_premium ??
        policy.coverage_amount ??
        0
    );
    if (!Number.isFinite(annual) || annual <= 0) return 0;
    return Math.round((annual / 12) * 100) / 100;
};

const UserInsuranceCompanyPolicies = () => {
    const { companyId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [company, setCompany] = useState(location.state?.company || null);
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const { startPayment, processingKey } = useInsurancePayment();

    useEffect(() => {
        fetchCompany();
        fetchPolicies();
    }, [companyId]);

    const fetchCompany = async () => {
        if (company) return;
        try {
            const response = await oneKlicks.read(
                "",
                "insurance_companies",
                { id: Number(companyId) },
                "id DESC",
                "",
                "",
                "0,1"
            );
            const companies = normaliseResponse(response);
            if (companies.length) setCompany(companies[0]);
        } catch (error) {
            console.error("Error fetching company info:", error);
        }
    };

    const fetchPolicies = async () => {
        setLoading(true);
        try {
            const response = await oneKlicks.read(
                "",
                "insurance_policies",
                { company_id: Number(companyId) },
                "created_at DESC",
                "",
                "",
                "0,200"
            );
            setPolicies(normaliseResponse(response));
        } catch (error) {
            console.error("Error fetching company policies:", error);
            toast.error("Unable to load policies for this company");
        } finally {
            setLoading(false);
        }
    };

    const handleViewPolicy = (policy) => {
        setSelectedPolicy(policy);
        setShowPolicyModal(true);
    };

    const handleStartPay = (policy) => {
        const monthlyPremium = calculateMonthlyPremium(policy);
        if (!monthlyPremium) {
            toast.error("Premium information missing for this policy.");
            return;
        }
        startPayment({
            referenceId: `company-policy-${policy.id}`,
            amount: monthlyPremium,
            policyMeta: {
                policyId: policy.id,
                policyNumber: policy.policy_number || policy.policy_code || `POL-${policy.id}`,
                product: policy.coverage_type || policy.policy_name || "Insurance Plan",
                insurer: company?.company_name || company?.name,
                companyId: policy.company_id || company?.id || null,
                checkoutTitle: company?.company_name || "Insurance Payment",
                notes: "Monthly premium payment",
            },
            description: `Monthly premium for ${policy.policy_number || policy.coverage_type || company?.company_name || "insurance plan"}`,
        });
    };

    const getStatusBadge = (status) => {
        const s = (status || "").toLowerCase();
        let variant = "info", label = status || "Pending";
        if (s === "active") variant = "success";
        else if (s === "expired" || s === "inactive") variant = "danger";
        return (
            <Badge bg={variant} style={{ borderRadius: '5px', padding: '5px 10px', fontSize: '11px', textTransform: 'capitalize', fontWeight: '600' }}>
                {label}
            </Badge>
        );
    };

    const pageTitle = company?.company_name || company?.name || `Company #${companyId}`;

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <style>
                {`
                    @media (max-width: 767px) {
                        .dc-table-responsive thead {
                            display: none;
                        }
                        .dc-table-responsive tbody tr {
                            display: block;
                            margin-bottom: 20px;
                            border: 1px solid #eee;
                            border-radius: 10px;
                            background: #fff;
                            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                            padding: 10px;
                        }
                        .dc-table-responsive tbody td {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 12px 15px !important;
                            border-bottom: 1px solid #f5f5f5;
                            text-align: right;
                            font-size: 13px;
                            min-height: 45px;
                        }
                        .dc-table-responsive tbody td:last-child {
                            border-bottom: 0;
                            margin-top: 10px;
                        }
                        .dc-table-responsive tbody td::before {
                            content: attr(data-label);
                            font-weight: 700;
                            color: #666;
                            text-transform: uppercase;
                            font-size: 10px;
                            letter-spacing: 0.5px;
                            text-align: left;
                            flex: 1;
                        }
                        .dc-table-responsive .d-flex.align-items-center {
                            justify-content: flex-end;
                            width: 100%;
                        }
                    }
                    .dc-btn-primary-modern {
                        background-color: var(--themecolor) !important;
                        border-color: var(--themecolor) !important;
                        color: #fff !important;
                        font-weight: 600;
                        border-radius: 5px;
                        transition: all 0.3s ease;
                    }
                    .dc-btn-primary-modern:hover {
                        background-color: #fff !important;
                        color: var(--themecolor) !important;
                        border-color: var(--themecolor) !important;
                    }
                    .modern-modal-header {
                        border-bottom: 1px solid #f0f0f0;
                        padding-bottom: 15px;
                        margin-bottom: 20px;
                    }
                    .policy-info-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 20px;
                    }
                    @media (max-width: 576px) {
                        .policy-info-grid {
                            grid-template-columns: 1fr;
                        }
                    }
                    .modal-close-btn:hover {
                        background-color: #ef4444 !important;
                        color: #fff !important;
                        border-color: #ef4444 !important;
                    }
                `}
            </style>
            <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
                <div className="container-fluid py-3">

                    {/* Company Header Card */}
                    <div className="card mb-4" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                        <div className="card-body" style={{ padding: '24px 30px' }}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-4">
                                <div className="d-flex align-items-center gap-4 flex-grow-1">
                                    <BSButton
                                        variant="light"
                                        onClick={() => navigate("/user-insurance")}
                                        style={{ width: '40px', height: '40px', borderRadius: '50%', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #eee' }}
                                    >
                                        <ArrowLeftOutlined />
                                    </BSButton>
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <h4 className="mb-0 font-weight-bold">{pageTitle}</h4>
                                            {getStatusBadge(company?.status)}
                                        </div>
                                        <div className="d-flex align-items-center gap-3 flex-wrap text-muted small">
                                            <span className="d-flex align-items-center gap-2">
                                                <MailOutlined style={{ color: 'var(--themecolor)' }} /> {company?.email || "N/A"}
                                            </span>
                                            <span className="d-flex align-items-center gap-2">
                                                <PhoneOutlined style={{ color: 'var(--themecolor)' }} /> {company?.phone || "N/A"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center bg-light p-3" style={{ borderRadius: '10px', minWidth: '150px', border: '1px solid #f0f0f0' }}>
                                    <small className="text-muted font-weight-bold d-block mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Coverage Plans</small>
                                    <h3 className="mb-0 font-weight-bold" style={{ color: 'var(--themecolor)' }}>{policies.length}</h3>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Policies List Card */}
                    <div className="card" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <div className="card-header bg-white border-bottom p-4">
                            <div className="d-flex align-items-center gap-2">
                                <SafetyOutlined style={{ color: 'var(--themecolor)', fontSize: '18px' }} />
                                <h6 className="mb-0 font-weight-bold">Available Coverage Plans</h6>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Discovering policies...</p>
                                </div>
                            ) : policies.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="dc-emptydata-holder">
                                        <div className="dc-emptydata">
                                            <div className="dc-emptydetails dc-emptyholder-sm">
                                                <span></span>
                                                <em className="text-muted">No policies available for this provider</em>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 align-middle dc-table-responsive">
                                        <thead className="bg-light">
                                            <tr style={{ whiteSpace: 'nowrap' }}>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Policy ID & Coverage</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Annual Premium</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Monthly Cost</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Valid Term</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Status</th>
                                                <th style={{ padding: '12px 20px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="border-top-0">
                                            {policies.map(policy => (
                                                <tr key={policy.id}>
                                                    <td data-label="Policy & Coverage" style={{ padding: '15px 20px' }}>
                                                        <div className="text-end text-md-start">
                                                            <div className="font-weight-bold text-dark" style={{ fontSize: '13px' }}>{policy.policy_number || policy.policy_code || `POL-${policy.id}`}</div>
                                                            <div style={{ fontSize: '11px', color: '#999' }}>{policy.coverage_type || "Standard Plan"}</div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Annual Premium" style={{ padding: '15px 20px' }}>
                                                        <div className="font-weight-bold text-dark">{formatCurrency(policy.premium_amount)}</div>
                                                    </td>
                                                    <td data-label="Monthly Cost" style={{ padding: '15px 20px' }}>
                                                        <div className="font-weight-bold text-primary" style={{ color: 'var(--themecolor)' }}>
                                                            {formatCurrency(calculateMonthlyPremium(policy))}
                                                        </div>
                                                    </td>
                                                    <td data-label="Term" style={{ padding: '15px 20px' }}>
                                                        <div className="text-muted" style={{ fontSize: '12px' }}>
                                                            <CalendarOutlined style={{ marginRight: '6px' }} />
                                                            {formatDate(policy.start_date)} - {formatDate(policy.end_date)}
                                                        </div>
                                                    </td>
                                                    <td data-label="Status" style={{ padding: '15px 20px' }}>
                                                        {getStatusBadge(policy.status)}
                                                    </td>
                                                    <td data-label="Actions" style={{ padding: '15px 20px' }}>
                                                        <div className="d-flex gap-2 justify-content-end align-items-center">
                                                            <Button
                                                                onClick={() => handleViewPolicy(policy)}
                                                                style={{ borderRadius: '5px', height: '34px', fontSize: '12px', fontWeight: '500' }}
                                                            >
                                                                Details
                                                            </Button>
                                                            <BSButton
                                                                className="dc-btn-primary-modern"
                                                                style={{ height: '34px', padding: '0 15px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                                onClick={() => handleStartPay(policy)}
                                                                disabled={processingKey === `company-policy-${policy.id}`}
                                                            >
                                                                {processingKey === `company-policy-${policy.id}` ? (
                                                                    <div className="spinner-border spinner-border-sm" role="status"></div>
                                                                ) : 'Pay Now'}
                                                            </BSButton>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Redesigned Policy Modal - Fixing Overlapping Issue */}
            <Modal
                show={showPolicyModal}
                onHide={() => setShowPolicyModal(false)}
                centered
                size="lg"
                className="dc-policy-modal"
                contentClassName="border-0 shadow-lg"
                style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.4)' }}
            >
                <div style={{ borderRadius: '15px', overflow: 'hidden', background: '#fff' }}>
                    <Modal.Header style={{ borderBottom: '1px solid #f0f0f0', padding: '20px 30px' }}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                            <div className="d-flex align-items-center gap-2">
                                <ContainerOutlined style={{ color: 'var(--themecolor)', fontSize: '20px' }} />
                                <Modal.Title className="font-weight-bold" style={{ fontSize: '18px', marginBottom: 0 }}>Policy Documentation</Modal.Title>
                            </div>
                            <button
                                onClick={() => setShowPolicyModal(false)}
                                style={{
                                    background: '#f8fafc',
                                    border: '1px solid #eee',
                                    borderRadius: '5px',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#666',
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                }}
                                className="modal-close-btn"
                            >
                                <CloseOutlined style={{ fontSize: '14px' }} />
                            </button>
                        </div>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '30px' }}>
                        {selectedPolicy && (
                            <div className="policy-details-wrapper">
                                {/* Top Summary Bar */}
                                <div className="bg-light p-4 rounded-3 mb-4 border d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div>
                                        <h4 className="mb-1 font-weight-bold" style={{ color: '#111827' }}>{selectedPolicy?.policy_number || `POL-${selectedPolicy?.id}`}</h4>
                                        <p className="text-muted mb-0 font-weight-bold" style={{ fontSize: '13px' }}>{selectedPolicy?.coverage_type || "General Insurance Plan"}</p>
                                    </div>
                                    {getStatusBadge(selectedPolicy?.status)}
                                </div>

                                {/* Information Grid - Clean & Non-Overlapping */}
                                <div className="policy-info-grid">
                                    <div className="info-item d-flex align-items-center gap-3 p-3 border rounded-3 bg-white shadow-sm">
                                        <div style={{ width: '42px', height: '42px', borderRadius: '10px', backgroundColor: '#eef2ff', color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                            <SafetyOutlined />
                                        </div>
                                        <div>
                                            <small className="text-muted font-weight-bold d-block mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Coverage Limit</small>
                                            <div className="font-weight-bold" style={{ fontSize: '15px' }}>{formatCurrency(selectedPolicy?.coverage_amount)}</div>
                                        </div>
                                    </div>

                                    <div className="info-item d-flex align-items-center gap-3 p-3 border rounded-3 bg-white shadow-sm">
                                        <div style={{ width: '42px', height: '42px', borderRadius: '10px', backgroundColor: '#fdf2f8', color: '#ec4899', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                            <WalletOutlined />
                                        </div>
                                        <div>
                                            <small className="text-muted font-weight-bold d-block mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Annual Premium</small>
                                            <div className="font-weight-bold" style={{ fontSize: '15px' }}>{formatCurrency(selectedPolicy?.premium_amount)}</div>
                                        </div>
                                    </div>

                                    <div className="info-item d-flex align-items-center gap-3 p-3 border rounded-3 bg-white shadow-sm" style={{ borderLeft: '4px solid var(--themecolor)' }}>
                                        <div style={{ width: '42px', height: '42px', borderRadius: '10px', backgroundColor: '#ecfdf5', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                            <DollarCircleOutlined />
                                        </div>
                                        <div>
                                            <small className="text-muted font-weight-bold d-block mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Monthly Installment</small>
                                            <div className="font-weight-bold text-primary" style={{ fontSize: '18px', color: 'var(--themecolor)' }}>{formatCurrency(calculateMonthlyPremium(selectedPolicy))}</div>
                                        </div>
                                    </div>

                                    <div className="info-item d-flex align-items-center gap-3 p-3 border rounded-3 bg-white shadow-sm">
                                        <div style={{ width: '42px', height: '42px', borderRadius: '10px', backgroundColor: '#fff7ed', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                            <CalendarOutlined />
                                        </div>
                                        <div>
                                            <small className="text-muted font-weight-bold d-block mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Validity Period</small>
                                            <div className="font-weight-bold" style={{ fontSize: '13px' }}>{formatDate(selectedPolicy?.start_date)} — {formatDate(selectedPolicy?.end_date)}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Plan Notes Section */}
                                {selectedPolicy?.notes && (
                                    <div className="mt-4 p-4 border rounded-3 bg-white shadow-sm">
                                        <h6 className="font-weight-bold mb-3 d-flex align-items-center gap-2" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#666' }}>
                                            <SolutionOutlined /> Plan Details & Benefits
                                        </h6>
                                        <p className="mb-0 text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>{selectedPolicy?.notes}</p>
                                    </div>
                                )}

                                {/* Payment Notice */}
                                <div className="mt-4 p-3 bg-light border-0 d-flex align-items-start gap-3" style={{ borderRadius: '10px' }}>
                                    <InfoCircleOutlined style={{ color: '#3b82f6', marginTop: '3px' }} />
                                    <div style={{ fontSize: '12px', color: '#475569' }}>
                                        <span className="font-weight-bold d-block mb-1">Payment Information:</span>
                                        Proceeding with payment will activate this policy subject to verification. The first installment covers the first month of coverage.
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: 'none', padding: '0 30px 30px 30px' }}>
                        {selectedPolicy && (
                            <div className="d-flex gap-3 w-100 justify-content-end">
                                <BSButton variant="light" onClick={() => setShowPolicyModal(false)} style={{ borderRadius: '5px', fontWeight: '600', padding: '10px 25px', border: '1px solid #ddd' }}>
                                    Close
                                </BSButton>
                                <BSButton
                                    className="dc-btn-primary-modern"
                                    style={{ padding: '10px 30px', border: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    onClick={() => handleStartPay(selectedPolicy)}
                                    disabled={processingKey === `company-policy-${selectedPolicy?.id}`}
                                >
                                    {processingKey === `company-policy-${selectedPolicy?.id}` ? (
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                    ) : <>Proceed to Payment <ArrowRightOutlined /></>}
                                </BSButton>
                            </div>
                        )}
                    </Modal.Footer>
                </div>
            </Modal>
        </section>
    );
};

export default UserInsuranceCompanyPolicies;

